import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROTULO_STATUS_PARCEIRO, type StatusParceiro } from '@reducee/contracts';
import { useState, type FormEvent } from 'react';

import { chamarApi, ErroDaApi } from '../api/cliente.ts';

import { CadastroDeOperadoras } from '../componentes/CadastroDeOperadoras.tsx';
import { CadastroDeOrigens } from '../componentes/CadastroDeOrigens.tsx';
import { ParedeDeAnuncios } from '../componentes/ParedeDeAnuncios.tsx';
import { RoteiroDeCadencia } from '../componentes/RoteiroDeCadencia.tsx';
import {
  RoteirosDeConferencia,
  type ModeloDeConferencia,
} from '../componentes/RoteirosDeConferencia.tsx';
import { TrilhaDeAuditoria } from '../componentes/TrilhaDeAuditoria.tsx';
import { UsuariasEPermissoes } from '../componentes/UsuariasEPermissoes.tsx';
import { Botao, CabecalhoDePagina, Cartao, LinhaDeLista, Pilula } from '../componentes/Superficie.tsx';
import { CampoTexto } from '../componentes/Campo.tsx';

type Painel = {
  parceiros: Array<{
    id: string;
    nome: string;
    documento: string | null;
    contato: string | null;
    status: StatusParceiro;
  }>;
  modelosChecklist: ModeloDeConferencia[];
  canais: Array<{
    id: string;
    nome: string;
    emailNotificacao: string | null;
    whatsappNotificacao: string | null;
  }>;
  integracoes: Array<{
    chave: string;
    nome: string;
    paginaId: string | null;
    formularioId: string | null;
    status: string;
    ativa: boolean;
  }>;
};

/**
 * Os quatro painéis da Administração da operação.
 *
 * A ordem é a do protótipo, e o rótulo é o que a Administradora lê no botão.
 * `chave` vira `data-teste` e identifica qual está aberto.
 */
const PAINEIS = [
  { chave: 'usuarias', rotulo: 'Usuários e permissões' },
  { chave: 'cadencias', rotulo: 'Roteiro de cadência' },
  { chave: 'auditoria', rotulo: 'Auditoria' },
  { chave: 'anuncios', rotulo: 'Anúncios da Meta' },
] as const;

type ChaveDePainel = (typeof PAINEIS)[number]['chave'];

/** A linha que separa o painel aberto dos botões, igual para os quatro. */
function PainelAberto({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="mt-4 border-t border-linha pt-4" data-teste="painel-aberto">
      {children}
    </div>
  );
}

/**
 * Parametrização da operação.
 *
 * Reúne o que se configura uma vez e se consulta sempre: parceiros, roteiros
 * de conferência, para onde vão os lembretes e o estado das integrações.
 * Segredo nenhum aparece aqui — o token de verificação da Meta vive em
 * variável de ambiente, fora do banco e fora da tela.
 */
export function PaginaConfiguracoes(): React.JSX.Element {
  const [aberto, setAberto] = useState<ChaveDePainel | null>(null);

  const consulta = useQuery({
    queryKey: ['configuracoes'],
    queryFn: () => chamarApi<Painel>('/configuracoes'),
  });

  const painel = consulta.data;

  return (
    <section>
      <CabecalhoDePagina
        area="Administração"
        titulo="Configurações"
        descricao="Parâmetros da operação. Alterações passam a valer para o trabalho novo."
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Cartao
          titulo="Administração da operação"
          descricao="Quem acessa o sistema, o roteiro de cadência e o rastro de quem mexeu em quê."
          className="lg:col-span-2"
        >
          <div className="flex flex-wrap gap-2">
            {PAINEIS.map((painelDaAdministracao) => (
              <button
                key={painelDaAdministracao.chave}
                type="button"
                onClick={() =>
                  setAberto((atual) =>
                    atual === painelDaAdministracao.chave ? null : painelDaAdministracao.chave,
                  )
                }
                aria-expanded={aberto === painelDaAdministracao.chave}
                className={`rounded-[10px] border px-3.25 py-2.5 text-base font-[750] transition hover:bg-marinho-suave ${
                  aberto === painelDaAdministracao.chave
                    ? 'border-marinho bg-marinho-suave'
                    : 'border-linha'
                }`}
                data-teste={`abrir-${painelDaAdministracao.chave}`}
              >
                {painelDaAdministracao.rotulo}
              </button>
            ))}
          </div>

          {/* Um por vez. Quatro painéis desse tamanho abertos juntos fazem de
              Configurações uma página de rolagem sem fim, e o que a pessoa veio
              ajustar — canal, parceiro, roteiro de conferência — some lá
              embaixo. Cada painel monta só depois do clique: quem abriu esta
              tela para mexer noutra coisa não paga consulta nenhuma. */}
          {aberto === 'usuarias' ? <PainelAberto><UsuariasEPermissoes /></PainelAberto> : null}
          {aberto === 'cadencias' ? <PainelAberto><RoteiroDeCadencia /></PainelAberto> : null}
          {aberto === 'auditoria' ? <PainelAberto><TrilhaDeAuditoria /></PainelAberto> : null}
          {aberto === 'anuncios' ? <ParedeDeAnuncios /> : null}
        </Cartao>

        <CanaisDeNotificacao canais={painel?.canais ?? []} />
        <Parceiros parceiros={painel?.parceiros ?? []} />
        <CadastroDeOperadoras />
        <CadastroDeOrigens />

        <Cartao
          titulo="Roteiros de conferência"
          descricao="Aplicados à implantação conforme o produto. Implantações em andamento mantêm o roteiro que já copiaram."
        >
          <RoteirosDeConferencia modelos={painel?.modelosChecklist ?? []} />
        </Cartao>

        <Cartao
          titulo="Integrações"
          descricao="O token de verificação fica no ambiente do servidor, nunca aqui."
        >
          {(painel?.integracoes ?? []).map((integracao) => (
            <LinhaDeLista
              key={integracao.chave}
              titulo={integracao.nome}
              detalhe={`Página: ${integracao.paginaId ?? '—'} · Formulário: ${integracao.formularioId ?? '—'}`}
              valor={<Pilula tom={integracao.ativa ? 'sucesso' : 'neutro'}>
                {integracao.status}
              </Pilula>}
            />
          ))}
          {painel?.integracoes.length === 0 ? (
            <p className="text-texto-suave">Nenhuma integração cadastrada.</p>
          ) : null}

          <ResgateDoLeadCenter />
        </Cartao>
      </div>
    </section>
  );
}

type ResultadoDoResgate = {
  avisosCriados: number;
  jaExistiam: number;
  ilegiveis: number;
  truncada: boolean;
};

/**
 * Resgate dos leads presos no Lead Center da Meta.
 *
 * O formulário é digitado a cada resgate, e não lido da integração salva: o
 * marketing cria formulário novo a partir do anterior em vez de editar, então o
 * formulário de hoje não é o de daqui a dois meses.
 *
 * A contagem fica na tela depois de pronto porque, sem ela, quem apertou o
 * botão não tem como saber se funcionou — e repetir "por via das dúvidas" é o
 * que faz alguém achar que duplicou tudo.
 */
function ResgateDoLeadCenter(): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [formularioId, definirFormularioId] = useState('');

  const resgatar = useMutation({
    mutationFn: (identificador: string) =>
      chamarApi<ResultadoDoResgate>('/configuracoes/integracoes/meta/resgate', {
        metodo: 'POST',
        corpo: { formularioId: identificador },
      }),
    onSuccess: () => {
      // O Lead nasce depois, quando o worker consome a fila, então esta
      // invalidação não o traz — ela só evita que a lista fique com o retrato
      // de antes do resgate se a pessoa navegar até lá em seguida.
      void clienteDeConsultas.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  const resultado = resgatar.data;

  return (
    <form
      data-teste="formulario-resgate-meta"
      className="mt-4 border-t border-linha pt-4"
      onSubmit={(evento) => {
        evento.preventDefault();
        resgatar.mutate(formularioId.trim());
      }}
    >
      <p className="font-medium">Resgatar do Lead Center</p>
      <p className="mb-3 text-texto-suave">
        Traz os leads que a Meta guardou enquanto a integração esteve fora do ar. Repetir é
        seguro: quem já entrou não vem de novo.
      </p>

      <CampoTexto
        rotulo="Formulário"
        value={formularioId}
        onChange={(evento) => definirFormularioId(evento.target.value)}
        inputMode="numeric"
        maxLength={32}
        placeholder="Identificador do formulário na Meta"
        dica="O identificador muda a cada formulário novo criado no gerenciador."
      />

      <Botao
        tipo="submit"
        variante="primario"
        desabilitado={formularioId.trim() === '' || resgatar.isPending}
      >
        {resgatar.isPending ? 'Resgatando…' : 'Resgatar leads'}
      </Botao>

      {resultado ? (
        <div data-teste="resultado-do-resgate" className="mt-3">
          {/*
            "Avisos", e não "Leads": o Lead nasce logo em seguida, quando o
            processamento pergunta à Meta quem preencheu — e é lá que um lead
            que ela não reconheça falha. Dizer "12 leads trazidos" aqui
            prometeria um número que ainda pode encolher.
          */}
          <p>
            {resultado.avisosCriados} {resultado.avisosCriados === 1 ? 'trazido' : 'trazidos'} ·{' '}
            {resultado.jaExistiam} já existiam · {resultado.ilegiveis} ilegíveis
          </p>
          <p className="text-texto-suave">
            Viram Leads na lista em instantes, conforme o processamento avança.
          </p>
          {resultado.truncada ? (
            <p className="mt-2 text-alerta">
              O formulário tem mais leads do que coube nesta busca, e ficou gente para trás.
              Repetir o resgate percorre as mesmas páginas: fale com quem cuida da integração.
            </p>
          ) : null}
        </div>
      ) : null}

      {resgatar.isError ? (
        <p className="mt-3 text-erro">
          {resgatar.error instanceof ErroDaApi
            ? resgatar.error.message
            : 'Não foi possível resgatar agora.'}
        </p>
      ) : null}
    </form>
  );
}

function CanaisDeNotificacao({
  canais,
}: {
  canais: Painel['canais'];
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();

  const salvar = useMutation({
    mutationFn: ({
      id,
      emailNotificacao,
      whatsappNotificacao,
    }: {
      id: string;
      emailNotificacao: string | null;
      whatsappNotificacao: string | null;
    }) =>
      chamarApi(`/configuracoes/canais/${id}`, {
        metodo: 'PUT',
        corpo: { emailNotificacao, whatsappNotificacao },
      }),
    onSuccess: () => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['configuracoes'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['revisoes-pendentes'] });
    },
  });

  return (
    <Cartao
      titulo="Canais de notificação"
      descricao="Para onde vão os lembretes de revisão. Separado do e-mail de login: um identifica, o outro notifica."
    >
      <div className="space-y-4">
        {canais.map((canal) => (
          <form
            key={canal.id}
            className="rounded-lg border border-linha p-3"
            onSubmit={(evento: FormEvent<HTMLFormElement>) => {
              evento.preventDefault();
              const dados = new FormData(evento.currentTarget);
              salvar.mutate({
                id: canal.id,
                emailNotificacao: (dados.get('email') as string) || null,
                whatsappNotificacao: (dados.get('whatsapp') as string) || null,
              });
            }}
          >
            <p className="font-medium text-marinho">{canal.nome}</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <label className="block text-xs font-medium text-texto-suave">
                E-mail
                <input
                  name="email"
                  type="email"
                  defaultValue={canal.emailNotificacao ?? ''}
                  placeholder="nome@reduceconsultoria.com.br"
                  className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm outline-none focus:border-marinho-claro"
                />
              </label>
              <label className="block text-xs font-medium text-texto-suave">
                WhatsApp
                <input
                  name="whatsapp"
                  defaultValue={canal.whatsappNotificacao ?? ''}
                  placeholder="5571999999999"
                  className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm outline-none focus:border-marinho-claro"
                />
              </label>
            </div>
            <div className="mt-2 flex justify-end">
              <Botao tipo="submit" desabilitado={salvar.isPending}>
                Salvar contatos
              </Botao>
            </div>
          </form>
        ))}
      </div>
    </Cartao>
  );
}

function Parceiros({ parceiros }: { parceiros: Painel['parceiros'] }): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [nome, definirNome] = useState('');
  const [documento, definirDocumento] = useState('');

  const salvar = useMutation({
    mutationFn: () =>
      chamarApi('/configuracoes/parceiros', {
        metodo: 'PUT',
        corpo: { nome, documento: documento || undefined, status: 'ATIVO' },
      }),
    onSuccess: () => {
      definirNome('');
      definirDocumento('');
      void clienteDeConsultas.invalidateQueries({ queryKey: ['configuracoes'] });
    },
  });

  return (
    <Cartao
      titulo="Parceiros comerciais"
      descricao="Origem terceirizada de oportunidades. Saber de quem veio muda a análise de performance."
    >
      {parceiros.map((parceiro) => (
        <LinhaDeLista
          key={parceiro.id}
          titulo={parceiro.nome}
          detalhe={parceiro.documento ?? parceiro.contato ?? undefined}
          valor={<Pilula tom={parceiro.status === 'ATIVO' ? 'sucesso' : 'neutro'}>
            {ROTULO_STATUS_PARCEIRO[parceiro.status]}
          </Pilula>}
        />
      ))}
      {parceiros.length === 0 ? (
        <p className="text-texto-suave">Nenhum parceiro cadastrado.</p>
      ) : null}

      <form
        className="mt-4 flex flex-wrap items-end gap-2"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (nome.trim().length >= 2) salvar.mutate();
        }}
      >
        <label className="flex-1 text-xs font-medium text-texto-suave">
          Nome
          <input
            value={nome}
            onChange={(evento) => definirNome(evento.target.value)}
            className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm outline-none focus:border-marinho-claro"
          />
        </label>
        <label className="flex-1 text-xs font-medium text-texto-suave">
          CNPJ
          <input
            value={documento}
            onChange={(evento) => definirDocumento(evento.target.value)}
            className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm outline-none focus:border-marinho-claro"
          />
        </label>
        <Botao tipo="submit" variante="primario" desabilitado={salvar.isPending}>
          Adicionar
        </Botao>
      </form>

      {salvar.error ? (
        <p className="mt-2 text-sm text-erro">
          {salvar.error instanceof ErroDaApi
            ? salvar.error.message
            : 'Não foi possível salvar o parceiro.'}
        </p>
      ) : null}
    </Cartao>
  );
}
